import type { ButtonHTMLAttributes } from 'react';
import { cn } from './utils';

type CommonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> & {
  size?: 'sm' | 'md';
  className?: string;
};

type ButtonProps =
  | (CommonProps & { variant?: 'primary' | 'secondary' | 'destructive'; 'aria-label'?: string })
  | (CommonProps & { variant: 'icon'; 'aria-label': string });

const VARIANT_STYLES: Record<'primary' | 'secondary' | 'destructive' | 'icon', string> = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700',
  secondary: 'border border-border bg-card text-foreground hover:bg-muted',
  // SIL-1541: destructive-outline (Withdraw and friends) - red text/border on the
  // card surface, never a filled red that competes with the primary action.
  destructive:
    'border border-red-300 bg-card text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950/40',
  icon: 'p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground',
};

const SIZE_STYLES: Record<'sm' | 'md', string> = {
  md: 'px-4 py-2 text-sm',
  sm: 'px-3 py-1.5 text-xs',
};

/**
 * Shared button primitive for primary/secondary/icon actions. Icon variant
 * ignores size (fixed p-1.5) and requires an aria-label — icon-only buttons
 * with no accessible name are a recurring a11y gap.
 *
 * `inline-flex items-center justify-center` is load-bearing, not decorative:
 * a bare `<button>` is inline-block, and Tailwind's preflight sets every
 * `svg` to `display: block`. Without an explicit flex context, an icon
 * child renders as a block box inside that inline-block button - it breaks
 * onto its own line above the label instead of sitting beside it, and any
 * `gap-*` a caller passes via `className` (e.g. `className="gap-1"`) is a
 * no-op because `gap` only applies to flex/grid containers. Setting the
 * flex context here, once, means every caller's `gap-*` utility works and
 * icon+label buttons lay out inline everywhere this component is used.
 */
export function Button({ variant = 'primary', size = 'md', className, ...props }: ButtonProps) {
  return (
    <button
      type="button"
      className={cn(
        'inline-flex items-center justify-center rounded-md font-medium transition-colors disabled:opacity-50',
        VARIANT_STYLES[variant],
        variant !== 'icon' && SIZE_STYLES[size],
        className,
      )}
      {...props}
    />
  );
}
